import { createFileRoute, Link } from "@tanstack/react-router"

import gfcLogo from "@/assets/company/GFC.png"
import madronaLogo from "@/assets/company/Madrona.png"
import { findBlogAuthor } from "@/lib/blog-authors"
import { marketingPages } from "@/lib/marketing-pages"
import { seoMeta } from "@/lib/seo"
import { team } from "@/lib/team"
import { ResponsiveImage } from "@/routes/-components/site/ResponsiveImage"
import { SiteContentColumn } from "@/routes/-components/site/SiteContentColumn"
import { SiteSection } from "@/routes/-components/site/SiteSection"

// Marvin, Entire's robot and the voice of the dispatches, leads the team grid
// and links to the brand page, where he lives among the brand assets.
const marvin = findBlogAuthor("Marvin")!
const members = [{ ...marvin, href: "/brand" as const }, ...team]

export const Route = createFileRoute("/about/company/")({
  head: () =>
    seoMeta({
      title: "Company",
      description: marketingPages["/about/company"].description,
      url: "https://entire.com/about/company",
    }),
  component: CompanyPage,
})

type InvestorLogoSize = "sm" | "md" | "lg" | "xl"

interface Investor {
  name: string
  logo: string
  size: InvestorLogoSize
  width: number
  height: number
  // Invert colors in dark mode (for near-black logos).
  darkInvert?: boolean
  // Recolor the logo to solid white in dark mode (for dark, non-black logos on
  // transparent backgrounds, where a plain invert would shift the hue).
  darkWhite?: boolean
}

const investorLogoClasses: Record<InvestorLogoSize, string> = {
  sm: "max-h-12 max-w-[80%]",
  md: "max-h-16 max-w-[80%]",
  lg: "max-h-24 max-w-[80%]",
  xl: "max-h-44 max-w-full",
}

const investors: Investor[] = [
  {
    name: "Felicis",
    logo: "/images/investors/Felicis.svg",
    size: "md",
    width: 771,
    height: 197,
  },
  {
    name: "Madrona",
    logo: madronaLogo,
    size: "lg",
    width: 800,
    height: 600,
    darkWhite: true,
  },
  {
    name: "M12",
    logo: "/images/investors/M12.png",
    size: "lg",
    width: 800,
    height: 516,
  },
  {
    name: "Basis Set Ventures",
    logo: "/images/investors/BasisSet.png",
    size: "md",
    width: 2459,
    height: 360,
    darkInvert: true,
  },
  {
    name: "Global Founders Capital",
    logo: gfcLogo,
    size: "sm",
    width: 1600,
    height: 657,
    darkWhite: true,
  },
  {
    name: "Picus Capital",
    logo: "/images/investors/Picus.svg",
    size: "xl",
    width: 142,
    height: 57,
    darkInvert: true,
  },
  {
    name: "20VC",
    logo: "/images/investors/20VC.svg",
    size: "sm",
    width: 1384,
    height: 423,
    darkInvert: true,
  },
  {
    name: "Cherry Ventures",
    logo: "/images/investors/Cherry_Logo_Two-row_Pink.svg",
    size: "lg",
    width: 1661,
    height: 1200,
  },
]

const values = [
  {
    title: "Autonomy",
    body: "We're a company built by developers, for developers. We believe in autonomy, in honing your personal craftsmanship, and in building with deep passion and genuine fun.",
    image: "/images/company/value-autonomy.jpg",
    imageAvif: "/images/company/value-autonomy.avif",
  },
  {
    title: "Inclusion",
    body: "Modeled off the ideals of the open source community, we are building a culture of inclusion and belonging, where all people and ideas are respected.",
    image: "/images/company/value-inclusion.jpg",
    imageAvif: "/images/company/value-inclusion.avif",
  },
  {
    title: "Professionalism",
    body: "Grounded in honesty and integrity, our culture values transparent feedback, colleagues who support and help one another, where no questions are invalid and go without an answer.",
    image: "/images/company/value-professionalism.jpg",
    imageAvif: "/images/company/value-professionalism.avif",
  },
]

const operatingPrinciples = [
  {
    title: "Small squads, clear ownership",
    body: "Two to four people is typical. One or more teams assemble around a time-boxed epic and disband when done. Often engineers or designers lead.",
  },
  {
    title: "One product, one roadmap",
    body: "We do not use OKRs or run A/B tests. We align on a north star and make decisions with judgment, customer insight, and taste.",
  },
  {
    title: "Short specs, high tempo, fast feedback",
    body: "Specs focus on context and intent. We ship with high tempo behind feature flags to internal users first, then invite select customers when it helps.",
  },
  {
    title: "Protect quality",
    body: "We keep a zero-bugs policy with SLAs. Bugs get fixed now, not later. We review a weekly bug dashboard to rebalance load and spot patterns.",
  },
  {
    title: "Constraints create clarity",
    body: "We ask why a feature matters, why now, and for whom. We keep the bar high by saying no and don't have endless discussions. Once we made a decision, we will only revisit the decision if there was a material change to the multiverse.",
  },
  {
    title: "Stay close to customers",
    body: "We build what customers need, not just what they ask for. Use common sense to balance customer feedback with our vision and roadmap. Customer obsessions means to meet with customers often, not using beliefs on what they want.",
  },
  {
    title: "Global remote team",
    body: "We work all around the world, from our homes, coffee shops, hotels, or coworking spaces. We all meet twice a year in person, and in the regions more often.",
  },
  {
    title: "Meetings and communications",
    body: "We design our day-to-day to be asynchronous where possible, synchronous when needed. Meetings should serve a clear purpose and not waste time.",
  },
  {
    title: "Regular updates",
    body: "We post regular updates in the #progress channel and be vulnerable if we feel not productive. Communicate in Slack as much as possible, so others understand what you do and need. Default to public channels over DMs.",
  },
  {
    title: "On call and accountable",
    body: "Every team member will be scheduled for on-call rotations. We hire globally and aim that rotations follow the sun. Blameless, fast turnaround, CEO takes accountability for the whole team for major incidents. If we fuck it up, it's on us.",
  },
]

function CompanyPage() {
  return (
    <>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn gap={3}>
          <h1 className="text-site-title-lg font-headline text-default">About</h1>
          <p className="text-site-body font-sans text-muted">
            Entire is a globally distributed, remote-first team with one clear mission: to build the
            world's next developer platform where humans and agents collaborate, learn, and ship
            together. We operate just as we believe the future of the interconnected developer
            community will work, with developers coming together across geographies to turn our
            biggest ideas, empowered by fleets of agents and shared context, into reality faster
            than was ever possible in the old days. This is the Entire way.
          </p>
        </SiteContentColumn>
      </SiteSection>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn gap={3}>
          <h2 className="text-site-title font-headline text-default">Values</h2>
          <p className="text-site-body font-sans text-muted">
            We are a company built by developers, for developers. Our culture is grounded in
            autonomy, inclusion, professionalism, transparent feedback, and deep care for craft.
          </p>
        </SiteContentColumn>
      </SiteSection>
      <SiteSection innerClassName="grid md:grid-cols-3">
        {values.map((value) => (
          <article
            key={value.title}
            className="relative flex min-h-120 overflow-hidden border-b border-subtle p-6 md:border-r md:border-b-0 md:last:border-r-0"
          >
            <ResponsiveImage
              src={value.image}
              avifSrc={value.imageAvif}
              alt=""
              width={1086}
              height={1222}
              pictureClassName="pointer-events-none absolute inset-0 size-full"
              className="size-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-linear-to-b from-black/10 to-black/45" />
            <div className="relative flex w-full flex-col justify-between gap-16">
              <h2 className="site-h1 text-white">{value.title}</h2>
              <p className="text-site-body max-w-90 font-sans text-white">{value.body}</p>
            </div>
          </article>
        ))}
      </SiteSection>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn>
          <h2 className="text-site-title font-headline text-default">How we operate</h2>
          <ol className="grid gap-8 md:grid-cols-2">
            {operatingPrinciples.map((principle, index) => (
              <li key={principle.title} className="flex flex-col gap-5">
                {/* The list numbers the principles for assistive tech; the badge shows it. */}
                <div
                  aria-hidden="true"
                  className="flex size-10 items-center justify-center overflow-hidden rounded-lg bg-floating text-default shadow-[0_0_0_1px_rgba(0,0,0,0.06)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.12)]"
                >
                  <span className="w-6 text-center font-mono text-sm leading-4 font-normal text-default">
                    {index + 1}
                  </span>
                </div>
                <div className="text-site-body flex flex-col gap-1 font-sans text-muted">
                  <h3 className="font-normal text-default">{principle.title}</h3>
                  <p>{principle.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </SiteContentColumn>
      </SiteSection>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn gap={3}>
          <h2 className="text-site-title font-headline text-default">Our investors</h2>
          <p className="text-site-body font-sans text-muted">
            In pursuit of our mission, we are proud to be backed with a $60 million seed investment
            by a group of leading international investors who all collectively believe that the time
            is now for software development to undergo its next platform shift.
          </p>
        </SiteContentColumn>
      </SiteSection>
      <SiteSection innerClassName="grid grid-cols-2 md:grid-cols-4">
        {investors.map((investor) => (
          <div
            key={investor.name}
            className="flex min-h-40 items-center justify-center border-r border-b border-subtle p-8 nth-[2n]:border-r-0 nth-last-[-n+2]:border-b-0 md:h-60 md:nth-[2n]:border-r md:nth-[4n]:border-r-0 md:nth-last-[-n+4]:border-b-0"
          >
            <ResponsiveImage
              src={investor.logo}
              alt={investor.name}
              width={investor.width}
              height={investor.height}
              className={`${investorLogoClasses[investor.size]} object-contain${
                investor.darkInvert ? " dark:invert" : ""
              }${investor.darkWhite ? " dark:brightness-0 dark:invert" : ""}`}
            />
          </div>
        ))}
      </SiteSection>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn>
          <div className="flex flex-col gap-3">
            <h2 className="text-site-title font-headline text-default">Team</h2>
            <p className="text-site-body font-sans text-muted">
              What unifies every Entiron is our passion for building developer tools. We have
              decades of experience in building startups, contributing to open-source projects, and
              shipping enterprise software. We live in the US, Australia, Germany, Spain, the
              Netherlands, and a growing list of countries. We go and hire the best talent, no
              matter where they live.
            </p>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
            {members.map((member) => {
              const card = (
                <>
                  <div className="relative aspect-245/306 overflow-hidden bg-white">
                    {/* The name sits right below the portrait. */}
                    <ResponsiveImage
                      src={member.image}
                      alt=""
                      width={500}
                      height={600}
                      pictureClassName="absolute inset-0 size-full"
                      className="size-full object-cover"
                    />
                  </div>
                  {/* Two lines on screen, one "Name, Role" line for screen readers and Markdown. */}
                  <p className="leading-6">
                    <span className="block font-semibold text-default">{member.name}</span>
                    <span className="sr-only">, </span>
                    <span className="block text-muted">{member.title}</span>
                  </p>
                </>
              )
              return (
                <li key={member.image}>
                  {"href" in member ? (
                    <Link to={member.href} className="flex flex-col gap-3">
                      {card}
                    </Link>
                  ) : (
                    <div className="flex flex-col gap-3">{card}</div>
                  )}
                </li>
              )
            })}
          </ul>
        </SiteContentColumn>
      </SiteSection>
    </>
  )
}
